import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import Product from '../types/product';

export interface CartItem extends Product {
    quantity: number;
}

interface CartContextType {
    items: CartItem[];
    addToCart: (product: Product) => void;
    removeFromCart: (productId: string) => void;
    updateQuantity: (productId: string, quantity: number) => void;
    clearCart: () => void;
    cartTotal: number;
    cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function safeGetItem(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function safeSetItem(key: string, value: string) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Ignore storage write failures
    }
}

function safeParseCart(raw: string | null): CartItem[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
    } catch {
        return [];
    }
}

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>(() => {
        const savedCart = safeGetItem('cart');
        return safeParseCart(savedCart);
    });
    const pendingToastsRef = useRef<string[]>([]);

    useEffect(() => {
        safeSetItem('cart', JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        if (pendingToastsRef.current.length > 0) {
            pendingToastsRef.current.forEach(message => {
                toast.success(message);
            });
            pendingToastsRef.current = [];
        }
    }, [items]);

    const addToCart = (product: Product) => {
        setItems(prevItems => {
            const exists = prevItems.some(item => item.productID === product.productID);
            const message = exists
                ? `Updated quantity for ${product.name}`
                : `${product.name} added to cart`;
            pendingToastsRef.current.push(message);

            const existingItem = prevItems.find(item => item.productID === product.productID);
            if (existingItem) {
                return prevItems.map(item =>
                    item.productID === product.productID
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prevItems, { ...product, quantity: 1 }];
        });
    };

    const removeFromCart = (productId: string) => {
        const itemToRemove = items.find(item => item.productID === productId);
        if (itemToRemove) {
            toast.success(`${itemToRemove.name} removed from cart`);
        }
        setItems(prevItems => prevItems.filter(item => item.productID !== productId));
    };

    const updateQuantity = (productId: string, quantity: number) => {
        if (quantity < 1) {
            removeFromCart(productId);
            return;
        }
        setItems(prevItems =>
            prevItems.map(item =>
                item.productID === productId
                    ? { ...item, quantity }
                    : item
            )
        );
    };

    const clearCart = () => {
        setItems([]);
    };

    const cartTotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
    const cartCount = items.reduce((count, item) => count + item.quantity, 0);

    return (
        <CartContext.Provider value={{
            items,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            cartTotal,
            cartCount
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

